import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { QuoteDto } from "@grizzly/shared";
import { quoteStatus } from "@grizzly/shared";
import { api } from "../lib/apiClient.js";
import type { ApiClientError } from "../lib/apiClient.js";
import { formatDateTime } from "../lib/format.js";
import { useI18n, type MessageKey } from "../i18n/index.js";
import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";
import { PageHeader } from "../components/ui/card.js";
import { DataTable, type Column } from "../components/ui/dataTable.js";
import { QuoteCreateDialog } from "../components/sales/quoteCreateDialog.js";

export function QuotesPage() {
  const { t } = useI18n();
  const [items, setItems] = useState<QuoteDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listQuotes();
      setItems(res.items);
      setError(null);
    } catch (err) {
      setError((err as ApiClientError).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const columns: Column<QuoteDto>[] = [
    {
      key: "number",
      header: t("sales.quote"),
      render: (r) => (
        <Link
          to={`/quotes/${r.id}`}
          className="tabular font-medium text-action-primary-text hover:underline"
        >
          {r.number}
        </Link>
      ),
    },
    {
      key: "customer",
      header: t("common.fields.customer"),
      render: (r) => r.customerName,
    },
    {
      key: "status",
      header: t("common.status"),
      render: (r) => (
        <Badge tone={quoteStatus(r.status).tone}>
          {t(`status.quote.${r.status}` as MessageKey)}
        </Badge>
      ),
    },
    {
      key: "when",
      header: t("common.created"),
      render: (r) => formatDateTime(r.createdAt),
    },
  ];

  return (
    <>
      <PageHeader
        title={t("nav.quotes")}
        description={t("sales.quotesDescription")}
        actions={
          <>
            <Button variant="secondary" asChild>
              <Link to="/rfqs">{t("sales.inquiries")}</Link>
            </Button>
            <Button variant="secondary" asChild>
              <Link to="/sales-orders">{t("sales.salesOrders")}</Link>
            </Button>
            <Button variant="primary" onClick={() => setOpen(true)}>
              {t("sales.newQuote")}
            </Button>
          </>
        }
      />
      {error ? (
        <p role="alert" className="mb-4 text-sm text-action-danger">
          {error}
        </p>
      ) : null}
      <DataTable
        caption={t("sales.quotes")}
        loading={loading}
        rows={items}
        rowKey={(r) => r.id}
        columns={columns}
      />
      <QuoteCreateDialog
        open={open}
        onOpenChange={setOpen}
        onCreated={() => void load()}
      />
    </>
  );
}
